/**
 * LastMonth Tab Mockup - UX/UI Audit Enhancement Preview
 *
 * This is a MOCKUP showing proposed improvements to the LastMonth tab.
 * It uses sample data and is completely self-contained.
 *
 * PROPOSED ENHANCEMENTS (from UX/UI Audit):
 * 1. Sticky Section Navigation - Jump between sections quickly
 * 2. Collapsible Sections - Reduce scroll fatigue
 * 3. Executive Summary at Top - Color-coded status with root cause
 * 4. 6-Month Historical Baselines - Context for all metrics
 * 5. "What Could Have Been" Analysis - Scenario modeling
 * 6. Funnel Waterfall Visualization - For consistency with ThisMonth
 *
 * Created: December 3, 2025 for UX/UI Audit
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LabelList
} from 'recharts';

// ============================================
// SAMPLE DATA - Mimics real data patterns
// ============================================
const LAST_MONTH = 'November';
const LAST_MONTH_YEAR = '2025';
const BUSINESS_DAYS_IN_MONTH = 21;

const SAMPLE_FINAL_RESULTS = {
  appsIn: 42,
  approved: 28,
  funded: 14,
  revenue: 485000,
  fundedAmount: 1150000,
  calls: 1245,
  sms: 682,
  totalTalkTime: 892,
  avgTalkTimePerCall: 0.72,
  // Goals
  appsGoal: 45,
  approvedGoal: 30,
  fundedGoal: 15,
  revenueGoal: 500000,
  // Achievements
  appsAchieved: 93.3,
  approvedAchieved: 93.3,
  fundedAchieved: 93.3,
  revenueAchieved: 97.0,
  // Conversion rates
  callsToApps: 3.4,
  smsToApps: 6.2,
  appsToApproved: 66.7,
  approvedToFunded: 50.0,
  // Submission metrics
  submissionsWon: 38,
  submissionsLost: 24,
  totalDeclines: 18,
  avgSubmissionsPerDeal: 2.1,
  successRate: 61.3
};

const SAMPLE_6MO_BASELINES = {
  appsIn: 38,
  approved: 25,
  funded: 12,
  revenue: 420000,
  callsToApps: 3.1,
  appsToApproved: 62.5,
  approvedToFunded: 48.0
};

const SAMPLE_WEEKLY_DATA = [
  { week: 'W1', apps: 12, approved: 8, funded: 4, revenue: 142, calls: 312, sms: 168 },
  { week: 'W2', apps: 11, approved: 7, funded: 4, revenue: 128, calls: 298, sms: 165 },
  { week: 'W3', apps: 10, approved: 7, funded: 3, revenue: 108, calls: 324, sms: 182 },
  { week: 'W4', apps: 9, approved: 6, funded: 3, revenue: 107, calls: 311, sms: 167 }
];

const SAMPLE_SUBMISSIONS_WEEKLY = [
  { week: 'W1', john: 8, sarah: 6, mike: 5, jane: 4 },
  { week: 'W2', john: 9, sarah: 7, mike: 6, jane: 5 },
  { week: 'W3', john: 7, sarah: 5, mike: 6, jane: 4 },
  { week: 'W4', john: 6, sarah: 5, mike: 4, jane: 3 }
];

const SAMPLE_REP_DATA = [
  { name: 'John', apps: 14, approved: 10, funded: 5, revenue: 172000, callToApp: 4.1, result: '🟢 Hit Goal' },
  { name: 'Sarah', apps: 12, approved: 8, funded: 4, revenue: 138000, callToApp: 3.8, result: '🟡 Close' },
  { name: 'Mike', apps: 9, approved: 6, funded: 3, revenue: 98000, callToApp: 3.2, result: '🟡 Close' },
  { name: 'Jane', apps: 7, approved: 4, funded: 2, revenue: 77000, callToApp: 2.6, result: '🔴 Missed' }
];

const SAMPLE_PACK_SOURCE = [
  { name: 'Web Lead', apps: 18, approved: 14, funded: 7, revenue: 215000, avgDealSize: 30714 },
  { name: 'Referral', apps: 12, approved: 8, funded: 4, revenue: 142000, avgDealSize: 35500 },
  { name: 'Cold Call', apps: 8, approved: 4, funded: 2, revenue: 78000, avgDealSize: 39000 },
  { name: 'Missing', apps: 4, approved: 2, funded: 1, revenue: 50000, avgDealSize: 50000 }
];

// "What Could Have Been" scenario
const SAMPLE_WHAT_COULD_HAVE_BEEN = {
  scenario: 'If Jane had matched team avg conversion (3.5% vs her 2.6%):',
  additionalApps: 3,
  additionalRevenue: 105000,
  wouldHaveHitGoal: true
};

// ============================================
// MAIN MOCKUP COMPONENT
// ============================================
export default function LastMonthTabMockup() {
  const [activeSection, setActiveSection] = useState('summary');
  const [collapsedSections, setCollapsedSections] = useState({});

  // Set unique browser tab title
  useEffect(() => {
    document.title = '🎨 MOCKUP: LastMonth Tab';
  }, []);

  // Refs for scrolling
  const sectionRefs = {
    summary: useRef(null),
    snapshot: useRef(null),
    submissions: useRef(null),
    goals: useRef(null),
    weekly: useRef(null),
    why: useRef(null)
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(value);

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  // Determine overall status
  const overallStatus = SAMPLE_FINAL_RESULTS.revenueAchieved >= 95 ? 'success' :
                        SAMPLE_FINAL_RESULTS.revenueAchieved >= 80 ? 'close' : 'missed';
  const statusEmoji = overallStatus === 'success' ? '🟢' : overallStatus === 'close' ? '🟡' : '🔴';
  const statusLabel = overallStatus === 'success' ? 'GOAL ACHIEVED' : overallStatus === 'close' ? 'CLOSE - ATTENTION NEEDED' : 'GOAL MISSED';
  const statusBorderColor = overallStatus === 'success' ? '#10b981' : overallStatus === 'close' ? '#f59e0b' : '#ef4444';
  const statusBgColor = overallStatus === 'success' ? '#f0fdf4' : overallStatus === 'close' ? '#fffbeb' : '#fef2f2';

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      {/* Header */}
      <div className="dashboard-header">
        <h1>Sales Performance Dashboard</h1>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          backgroundColor: '#fef3c7',
          padding: '8px 16px',
          borderRadius: '6px',
          border: '2px solid #f59e0b'
        }}>
          <span style={{ fontSize: '18px' }}>🎨</span>
          <span style={{ fontWeight: 600, color: '#92400e' }}>
            MOCKUP MODE - Proposed LastMonth Tab Enhancements | 🔧 P2 FIX: box-shadow standardized
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <nav className="tab-navigation">
        <button>Overview</button>
        <button>This Month</button>
        <button className="active">Last Month</button>
        <button>Historical</button>
      </nav>

      {/* ============================================ */}
      {/* ENHANCED: Sticky Section Navigation         */}
      {/* ============================================ */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        padding: '12px 0',
        borderBottom: '2px solid #e2e8f0',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginRight: '8px' }}>
            JUMP TO:
          </span>
          {[
            { id: 'summary', label: '📊 Summary', icon: '1' },
            { id: 'snapshot', label: '📈 Performance', icon: '2' },
            { id: 'submissions', label: '📤 Submissions', icon: '3' },
            { id: 'goals', label: '🎯 Goals', icon: '4' },
            { id: 'weekly', label: '📅 Weekly', icon: '5' },
            { id: 'why', label: '🔍 WHY', icon: '6' }
          ].map(section => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: activeSection === section.id ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                backgroundColor: activeSection === section.id ? '#eff6ff' : 'white',
                color: activeSection === section.id ? '#1e40af' : '#475569',
                fontWeight: activeSection === section.id ? 600 : 500,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {section.label}
            </button>
          ))}

          <div style={{
            marginLeft: 'auto',
            padding: '6px 12px',
            backgroundColor: '#d1fae5',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#065f46',
            fontWeight: 600
          }}>
            ✨ NEW: Sticky section navigation
          </div>
        </div>
      </div>

      <main className="dashboard-content">
        <div className="last-month-tab">
          <h2>Last Month: {LAST_MONTH} {LAST_MONTH_YEAR} (Final)</h2>
          <p className="tab-subtitle">Team Performance Review</p>

          {/* ============================================ */}
          {/* SECTION 1: EXECUTIVE SUMMARY                */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.summary}
            style={{
              scrollMarginTop: '80px',
              marginTop: '24px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('summary')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>📊 Executive Summary</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  High-level month results with root cause analysis
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: '#d1fae5',
                  borderRadius: '4px',
                  fontSize: '10px',
                  color: '#065f46',
                  fontWeight: 600
                }}>
                  ✨ Collapsible
                </span>
                <span style={{
                  fontSize: '20px',
                  color: '#64748b',
                  transform: collapsedSections.summary ? 'rotate(-90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>
                  ▼
                </span>
              </div>
            </div>

            {!collapsedSections.summary && (
              <>
                {/* Status Card */}
                <div style={{
                  padding: '20px',
                  marginBottom: '24px',
                  borderRadius: '12px',
                  border: `3px solid ${statusBorderColor}`,
                  backgroundColor: statusBgColor
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h2 style={{ margin: 0, fontSize: '22px', color: '#1e293b' }}>
                      {statusEmoji} {LAST_MONTH.toUpperCase()} STATUS: {statusLabel}
                    </h2>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                      {BUSINESS_DAYS_IN_MONTH} business days
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                    <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px' }}>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Revenue</div>
                      <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatCurrency(SAMPLE_FINAL_RESULTS.revenue)}</div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        of {formatCurrency(SAMPLE_FINAL_RESULTS.revenueGoal)} goal ({SAMPLE_FINAL_RESULTS.revenueAchieved}%)
                      </div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px' }}>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Apps In</div>
                      <div style={{ fontSize: '24px', fontWeight: 700 }}>{SAMPLE_FINAL_RESULTS.appsIn}</div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        of {SAMPLE_FINAL_RESULTS.appsGoal} goal ({SAMPLE_FINAL_RESULTS.appsAchieved}%)
                      </div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px' }}>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Funded</div>
                      <div style={{ fontSize: '24px', fontWeight: 700 }}>{SAMPLE_FINAL_RESULTS.funded}</div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        of {SAMPLE_FINAL_RESULTS.fundedGoal} goal ({SAMPLE_FINAL_RESULTS.fundedAchieved}%)
                      </div>
                    </div>
                  </div>

                  {/* Root Cause */}
                  <div style={{
                    padding: '12px 16px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <strong style={{ color: '#1e40af' }}>Root Cause Analysis:</strong>
                    <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#475569', lineHeight: 1.6 }}>
                      Missed revenue goal by {formatCurrency(SAMPLE_FINAL_RESULTS.revenueGoal - SAMPLE_FINAL_RESULTS.revenue)}.
                      Primary factor: Jane's conversion rate (2.6%) was 0.9pp below team average (3.5%), resulting in 3 fewer apps.
                      Secondary factor: Week 4 slowdown (-15% activity vs Week 3).
                    </p>
                  </div>
                </div>

                {/* "What Could Have Been" - UNIQUE to LastMonth */}
                <div style={{
                  padding: '16px 20px',
                  marginBottom: '24px',
                  borderRadius: '12px',
                  border: '2px dashed #8b5cf6',
                  backgroundColor: '#f5f3ff'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    <div style={{ fontSize: '28px' }}>🔮</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#5b21b6', marginBottom: '8px' }}>
                        WHAT COULD HAVE BEEN
                        <span style={{
                          marginLeft: '12px',
                          padding: '2px 8px',
                          backgroundColor: '#d1fae5',
                          borderRadius: '4px',
                          fontSize: '10px',
                          color: '#065f46'
                        }}>
                          ✨ UNIQUE TO LAST MONTH
                        </span>
                      </div>
                      <p style={{ fontSize: '14px', color: '#374151', marginBottom: '12px' }}>
                        {SAMPLE_WHAT_COULD_HAVE_BEEN.scenario}
                      </p>
                      <div style={{ display: 'flex', gap: '24px', fontSize: '14px' }}>
                        <span>
                          <strong>+{SAMPLE_WHAT_COULD_HAVE_BEEN.additionalApps}</strong> additional apps
                        </span>
                        <span>
                          <strong>+{formatCurrency(SAMPLE_WHAT_COULD_HAVE_BEEN.additionalRevenue)}</strong> additional revenue
                        </span>
                        <span style={{ color: '#10b981', fontWeight: 600 }}>
                          → {SAMPLE_WHAT_COULD_HAVE_BEEN.wouldHaveHitGoal ? 'Would have hit goal!' : 'Still missed goal'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 2: PERFORMANCE SNAPSHOT             */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.snapshot}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('snapshot')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>📈 Performance Snapshot</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Key metrics with 6-month historical baseline comparison
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.snapshot ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.snapshot && (
              <>
                {/* Metric Cards with Baseline */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <MetricCardWithBaseline
                    label="Apps In"
                    value={SAMPLE_FINAL_RESULTS.appsIn}
                    baseline={SAMPLE_6MO_BASELINES.appsIn}
                    formatValue={(v) => v}
                  />
                  <MetricCardWithBaseline
                    label="Approved"
                    value={SAMPLE_FINAL_RESULTS.approved}
                    baseline={SAMPLE_6MO_BASELINES.approved}
                    formatValue={(v) => v}
                  />
                  <MetricCardWithBaseline
                    label="Funded"
                    value={SAMPLE_FINAL_RESULTS.funded}
                    baseline={SAMPLE_6MO_BASELINES.funded}
                    formatValue={(v) => v}
                  />
                  <MetricCardWithBaseline
                    label="Revenue"
                    value={SAMPLE_FINAL_RESULTS.revenue}
                    baseline={SAMPLE_6MO_BASELINES.revenue}
                    formatValue={formatCurrency}
                  />
                </div>

                {/* Conversion Rates with Baseline */}
                <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>Conversion Rates</h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <ConversionCardWithBaseline label="Call → App" rate={SAMPLE_FINAL_RESULTS.callsToApps} baseline={SAMPLE_6MO_BASELINES.callsToApps} />
                  <ConversionCardWithBaseline label="SMS → App" rate={SAMPLE_FINAL_RESULTS.smsToApps} baseline={6.0} />
                  <ConversionCardWithBaseline label="App → Approved" rate={SAMPLE_FINAL_RESULTS.appsToApproved} baseline={SAMPLE_6MO_BASELINES.appsToApproved} />
                  <ConversionCardWithBaseline label="Approved → Funded" rate={SAMPLE_FINAL_RESULTS.approvedToFunded} baseline={SAMPLE_6MO_BASELINES.approvedToFunded} />
                </div>

                {/* Activity Volume Chart */}
                <div style={{
                  padding: '20px',
                  background: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Weekly Activity Trends</h3>
                  <ResponsiveContainer width="100%" height={320}>
                    <ComposedChart data={SAMPLE_WEEKLY_DATA}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="week" stroke="#4a5568" />
                      <YAxis yAxisId="left" stroke="#4a5568" />
                      <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="calls" fill="#3b82f6" name="Calls" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 12, fill: '#3b82f6', fontWeight: 600 }} />
                      <Bar yAxisId="left" dataKey="sms" fill="#9333ea" name="SMS" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 12, fill: '#9333ea', fontWeight: 600 }} />
                      <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} name="Revenue ($k)" label={{ position: 'top', fontSize: 11, fill: '#10b981', fontWeight: 600 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 3: LENDER SUBMISSIONS               */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.submissions}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('submissions')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h2 className="section-title" style={{ margin: 0 }}>📤 Lender Submissions</h2>
                  <span style={{
                    padding: '4px 8px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '4px',
                    fontSize: '10px',
                    color: '#92400e',
                    fontWeight: 600,
                    border: '1px solid #f59e0b'
                  }}>
                    🔍 DATA REVIEW NEEDED
                  </span>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Submission metrics and weekly breakdown by rep
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.submissions ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.submissions && (
              <>
                {/* Submission Metrics */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <SmallMetricCard label="Submissions Won" value={SAMPLE_FINAL_RESULTS.submissionsWon} color="#059669" />
                  <SmallMetricCard label="Submissions Lost" value={SAMPLE_FINAL_RESULTS.submissionsLost} color="#dc2626" />
                  <SmallMetricCard label="Total Declines" value={SAMPLE_FINAL_RESULTS.totalDeclines} color="#f59e0b" />
                  <SmallMetricCard label="Avg per Deal" value={SAMPLE_FINAL_RESULTS.avgSubmissionsPerDeal.toFixed(1)} />
                  <SmallMetricCard label="Success Rate" value={`${SAMPLE_FINAL_RESULTS.successRate.toFixed(0)}%`} color="#3b82f6" />
                </div>

                {/* Weekly Submissions by Rep Chart */}
                <div style={{
                  padding: '20px',
                  background: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>Weekly Submissions by Rep</h3>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#dbeafe',
                      borderRadius: '4px',
                      fontSize: '10px',
                      color: '#1e40af',
                      fontWeight: 600
                    }}>
                      🏷️ 12/5 LABELS
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={SAMPLE_SUBMISSIONS_WEEKLY}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="week" stroke="#4a5568" />
                      <YAxis stroke="#4a5568" />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="john" stroke="#3b82f6" strokeWidth={2} name="John">
                        <LabelList dataKey="john" position="top" style={{ fontSize: 10, fill: '#3b82f6', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="sarah" stroke="#10b981" strokeWidth={2} name="Sarah">
                        <LabelList dataKey="sarah" position="top" style={{ fontSize: 10, fill: '#10b981', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="mike" stroke="#f59e0b" strokeWidth={2} name="Mike">
                        <LabelList dataKey="mike" position="bottom" style={{ fontSize: 10, fill: '#f59e0b', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="jane" stroke="#ef4444" strokeWidth={2} name="Jane">
                        <LabelList dataKey="jane" position="bottom" style={{ fontSize: 10, fill: '#ef4444', fontWeight: 600 }} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 4: GOAL TRENDING                    */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.goals}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('goals')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>🎯 Goal Trending</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Final results and rep performance breakdown
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.goals ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.goals && (
              <>
                {/* Final Results Table */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600 }}>Monthly Final Results</h4>
                  <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600, color: '#64748b' }}>Metric</th>
                        <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600, color: '#64748b' }}>Actual</th>
                        <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600, color: '#64748b' }}>Goal</th>
                        <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600, color: '#64748b' }}>Shortfall</th>
                        <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600, color: '#64748b' }}>Achievement</th>
                      </tr>
                    </thead>
                    <tbody>
                      <FinalResultRow metric="Apps In" actual={42} goal={45} formatValue={(v) => v} />
                      <FinalResultRow metric="Approved" actual={28} goal={30} formatValue={(v) => v} />
                      <FinalResultRow metric="Funded" actual={14} goal={15} formatValue={(v) => v} />
                      <FinalResultRow metric="Revenue" actual={485000} goal={500000} formatValue={formatCurrency} />
                    </tbody>
                  </table>
                </div>

                {/* Rep Performance Table */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ marginBottom: '12px', fontSize: '17px', fontWeight: 600 }}>👥 Rep Performance</h4>
                  <table style={{ width: '100%', fontSize: '15px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ textAlign: 'left', padding: '10px 12px' }}>Rep</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Apps</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Approved</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Funded</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Revenue</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Call→App</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SAMPLE_REP_DATA.map((rep, idx) => (
                        <tr key={rep.name} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 500 }}>{rep.name}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.apps}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.approved}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.funded}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(rep.revenue)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.callToApp}%</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.result}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 5: WEEKLY BREAKDOWN                 */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.weekly}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('weekly')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>📅 Weekly Breakdown</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Week-by-week performance summary
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.weekly ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.weekly && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Week</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Calls</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>SMS</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Apps</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Approved</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Funded</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SAMPLE_WEEKLY_DATA.map((week, idx) => (
                      <tr key={week.week} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                        <td style={{ padding: '12px', fontWeight: 600 }}>{week.week}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{week.calls}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{week.sms}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{week.apps}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{week.approved}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{week.funded}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(week.revenue * 1000)}</td>
                      </tr>
                    ))}
                    {/* Total Row */}
                    <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 700, borderTop: '2px solid #e2e8f0' }}>
                      <td style={{ padding: '12px' }}>TOTAL</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_RESULTS.calls}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_RESULTS.sms}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_RESULTS.appsIn}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_RESULTS.approved}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{SAMPLE_FINAL_RESULTS.funded}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(SAMPLE_FINAL_RESULTS.revenue)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 6: WHY ANALYSIS                     */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.why}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('why')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>🔍 The "WHY" Analysis</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Why did we perform this way?
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.why ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.why && (
              <>
                {/* The Funnel */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ borderTop: '3px double #3b82f6', paddingTop: '16px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e40af', letterSpacing: '0.5px' }}>THE FUNNEL</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Month-end funnel results</div>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    padding: '20px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}>
                    <FunnelStage label="Calls" value={SAMPLE_FINAL_RESULTS.calls} color="#3b82f6" />
                    <FunnelArrow rate={`${SAMPLE_FINAL_RESULTS.callsToApps}%`} />
                    <FunnelStage label="Apps In" value={SAMPLE_FINAL_RESULTS.appsIn} color="#10b981" />
                    <FunnelArrow rate={`${SAMPLE_FINAL_RESULTS.appsToApproved}%`} />
                    <FunnelStage label="Approved" value={SAMPLE_FINAL_RESULTS.approved} color="#8b5cf6" />
                    <FunnelArrow rate={`${SAMPLE_FINAL_RESULTS.approvedToFunded}%`} />
                    <FunnelStage label="Funded" value={SAMPLE_FINAL_RESULTS.funded} color="#f59e0b" />
                    <FunnelArrow rate="→" />
                    <FunnelStage label="Revenue" value={`$${Math.round(SAMPLE_FINAL_RESULTS.revenue / 1000)}K`} color="#10b981" />
                  </div>
                </div>

                {/* Performance by Source */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '12px' }}>
                    Performance by Source
                  </h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>Source</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>Apps</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>Approved</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>Funded</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>Revenue</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>Avg Deal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SAMPLE_PACK_SOURCE.map((source, idx) => (
                        <tr key={source.name} style={{
                          borderBottom: '1px solid #e2e8f0',
                          background: source.name === 'Missing' ? '#fef3c7' : (idx % 2 === 0 ? 'white' : '#f9fafb')
                        }}>
                          <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                            {source.name === 'Missing' ? '⚠️ ' : ''}{source.name}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{source.apps}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{source.approved}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{source.funded}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(source.revenue)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(source.avgDealSize)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

        </div>
      </main>

      {/* Enhancement Legend */}
      <div style={{
        marginTop: '32px',
        padding: '20px',
        backgroundColor: '#eff6ff',
        borderRadius: '8px',
        border: '2px solid #3b82f6'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#1e40af' }}>
          Enhancements in This Mockup:
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          <EnhancementItem
            title="1. Sticky Section Navigation"
            description="Jump between sections quickly without scrolling."
          />
          <EnhancementItem
            title="2. Collapsible Sections"
            description="Click section headers to collapse/expand."
          />
          <EnhancementItem
            title="3. Executive Summary"
            description="Color-coded status with root cause analysis at top."
          />
          <EnhancementItem
            title="4. 6-Month Baselines"
            description="All metrics show comparison to 6-month historical average."
          />
          <EnhancementItem
            title="5. 'What Could Have Been'"
            description="Scenario analysis unique to Last Month tab."
          />
        </div>
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function MetricCardWithBaseline({ label, value, baseline, formatValue }) {
  const displayValue = formatValue(value);
  const displayBaseline = formatValue(baseline);
  const diff = value - baseline;
  const isUp = diff >= 0;

  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>{displayValue}</div>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        <strong>6-Mo Avg:</strong> {displayBaseline}
        <span style={{ marginLeft: '8px', color: isUp ? '#10b981' : '#ef4444', fontWeight: 600 }}>
          {isUp ? '↗' : '↘'} {isUp ? '+' : ''}{typeof value === 'number' && value > 1000 ? formatValue(diff) : diff}
        </span>
      </div>
    </div>
  );
}

function SmallMetricCard({ label, value, color }) {
  return (
    <div style={{
      padding: '12px',
      backgroundColor: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: color || '#1e293b' }}>{value}</div>
    </div>
  );
}

function ConversionCardWithBaseline({ label, rate, baseline }) {
  const diff = rate - baseline;
  const isUp = diff >= 0;

  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>{rate.toFixed(1)}%</div>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        <strong>6-Mo Avg:</strong> {baseline.toFixed(1)}%
        <span style={{ marginLeft: '6px', color: isUp ? '#10b981' : '#ef4444', fontWeight: 600 }}>
          {isUp ? '↗' : '↘'} {isUp ? '+' : ''}{diff.toFixed(1)}pp
        </span>
      </div>
    </div>
  );
}

function FinalResultRow({ metric, actual, goal, formatValue }) {
  const shortfall = goal - actual;
  const achievement = ((actual / goal) * 100).toFixed(1);

  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
      <td style={{ padding: '8px', fontWeight: 500 }}>{metric}</td>
      <td style={{ padding: '8px', textAlign: 'right' }}>{formatValue(actual)}</td>
      <td style={{ padding: '8px', textAlign: 'right' }}>{formatValue(goal)}</td>
      <td style={{ padding: '8px', textAlign: 'right', color: shortfall > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
        {shortfall > 0 ? `-${formatValue(shortfall)}` : '✓'}
      </td>
      <td style={{ padding: '8px', textAlign: 'right' }}>
        <span style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 600,
          backgroundColor: parseFloat(achievement) >= 100 ? '#d1fae5' : parseFloat(achievement) >= 90 ? '#fef3c7' : '#fee2e2',
          color: parseFloat(achievement) >= 100 ? '#065f46' : parseFloat(achievement) >= 90 ? '#92400e' : '#991b1b'
        }}>
          {achievement}%
        </span>
      </td>
    </tr>
  );
}

function FunnelStage({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', minWidth: '70px' }}>
      <div style={{
        backgroundColor: color,
        color: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        fontWeight: 700,
        fontSize: '20px',
        marginBottom: '8px'
      }}>
        {value}
      </div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: '#475569', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );
}

function FunnelArrow({ rate }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontSize: '24px', color: '#cbd5e1' }}>→</div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>{rate}</div>
    </div>
  );
}

function EnhancementItem({ title, description }) {
  return (
    <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '6px' }}>
      <strong style={{ color: '#10b981' }}>{title}</strong>
      <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b' }}>{description}</p>
    </div>
  );
}
